import { useEffect, useState } from 'react';
import axios from 'axios';
import { Mail, CheckCircle, AlertTriangle, AlertCircle, Inbox, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

export default function EmailDeliveryCard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hours, setHours] = useState(24);

  const fetchStats = async (h = hours) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/email-events/stats?hours=${h}`, {
        withCredentials: true,
      });
      setStats(res.data);
    } catch {
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(hours); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [hours]);

  const counts = stats?.counts || {};
  const rates = stats?.rates || {};
  const healthy = stats?.healthy ?? true;
  const webhookConfigured = stats?.webhook_configured;
  const noEvents = !loading && (counts.sent ?? 0) === 0 && (counts.delivered ?? 0) === 0 && (counts.bounced ?? 0) === 0;

  return (
    <Card data-testid="email-delivery-card" className="mb-6">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between gap-2 text-base">
          <span className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Email Delivery
            {!loading && (
              <Badge
                variant={healthy ? 'default' : 'destructive'}
                className={healthy ? 'bg-green-100 text-green-700 hover:bg-green-100' : ''}
              >
                {healthy ? 'Healthy' : 'Issues'}
              </Badge>
            )}
          </span>
          <span className="flex items-center gap-2">
            <select
              value={hours}
              onChange={(e) => setHours(Number(e.target.value))}
              className="text-xs border rounded px-2 py-1 bg-background"
              data-testid="email-stats-window"
            >
              <option value={1}>last 1 h</option>
              <option value={24}>last 24 h</option>
              <option value={168}>last 7 d</option>
              <option value={720}>last 30 d</option>
            </select>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => fetchStats()}
              disabled={loading}
              data-testid="email-stats-refresh"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!webhookConfigured && (
          <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 mb-3 text-amber-800">
            <AlertCircle className="h-3.5 w-3.5 inline mr-1" />
            Resend webhook secret not configured — events won't be tracked.
            Add <code className="bg-amber-100 px-1 rounded">RESEND_WEBHOOK_SECRET</code> to backend env.
          </div>
        )}
        {webhookConfigured && noEvents && (
          <p className="text-xs text-muted-foreground mb-3">
            No events recorded yet in this window. If you've just configured the webhook in Resend
            (URL: <code className="bg-muted px-1 rounded">/api/email-events/webhook</code>),
            stats will populate as emails are sent.
          </p>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Sent" value={counts.sent ?? 0} icon={Inbox} />
          <Stat
            label="Delivered"
            value={counts.delivered ?? 0}
            icon={CheckCircle}
            sub={rates.delivery_pct != null ? `${rates.delivery_pct}%` : null}
            tone="green"
          />
          <Stat
            label="Bounced"
            value={counts.bounced ?? 0}
            icon={AlertTriangle}
            sub={rates.bounce_pct != null ? `${rates.bounce_pct}%` : null}
            tone={(counts.bounced ?? 0) > 0 ? 'red' : 'muted'}
          />
          <Stat
            label="Complaints"
            value={counts.complained ?? 0}
            icon={AlertCircle}
            tone={(counts.complained ?? 0) > 0 ? 'red' : 'muted'}
          />
        </div>

        {stats?.recent_bounces?.length > 0 && (
          <div className="mt-4 border-t pt-3">
            <p className="text-xs font-medium mb-2">Recent bounces</p>
            <ul className="space-y-1">
              {stats.recent_bounces.map((b, i) => (
                <li key={i} className="text-xs text-muted-foreground flex justify-between gap-3">
                  <span className="truncate">
                    <span className="font-mono">{b.to || '?'}</span>
                    {b.subject ? <span className="ml-2 italic">{b.subject}</span> : null}
                  </span>
                  <span className="text-red-600 truncate max-w-[60%]" title={b.reason}>
                    {b.reason || 'unknown'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, sub, icon: Icon, tone = 'muted' }) {
  const toneClass = {
    green: 'text-green-700',
    red: 'text-red-600',
    muted: 'text-foreground',
  }[tone];
  return (
    <div className="border rounded-lg p-3 bg-card">
      <div className="text-xs text-muted-foreground flex items-center gap-1">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className={`text-2xl font-semibold mt-1 ${toneClass}`}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}
